// Comparte una conexión SSE entre TODAS las pestañas del navegador (patrón "pestaña líder").
//
// Problema que resuelve: en HTTP/1.1 (entorno local) el navegador limita ~6 conexiones por
// host; con varios streams SSE por pestaña, duplicar pestañas agota el pool y las peticiones
// normales se quedan colgadas. Con este helper solo UNA pestaña (la líder) mantiene la
// conexión real; el resto recibe los eventos retransmitidos por BroadcastChannel.
//
// Mecánica:
//  - Liderazgo por Web Locks: todas las pestañas piden el lock exclusivo del canal; solo una
//    lo obtiene (líder) y abre la SSE. Si la líder se cierra o se desconecta, el navegador
//    libera el lock y la siguiente en cola pasa a líder y conecta.
//  - La líder retransmite cada evento por BroadcastChannel('sse:<canal>'); las seguidoras
//    lo entregan a sus suscriptores como si fuera suyo.
//  - Una seguidora recién llegada envía 'hola' y la líder le reenvía el último evento
//    (equivalente al replay de lastEvent que hace el servidor a los que conectan tarde).
//  - Fallback: sin BroadcastChannel o Web Locks (WebViews antiguas de Cordova), cada pestaña
//    conecta directamente como siempre.

function soportado() {
    try {
        return typeof BroadcastChannel !== 'undefined'
            && typeof navigator !== 'undefined'
            && navigator.locks && typeof navigator.locks.request === 'function';
    } catch (e) {
        return false;
    }
}

/**
 * Conecta un canal SSE compartido.
 * @param {string} nombre  nombre lógico del canal (p. ej. 'alertas').
 * @param {object} opciones
 *   - abrir(emitir): SOLO la ejecuta la pestaña líder. Debe crear la conexión real
 *     (EventSourcePolyfill) y llamar a emitir(payload) por cada evento. Devuelve un objeto
 *     con close() para poder cerrarla.
 *   - onEvento(payload): se invoca en ESTA pestaña por cada evento (venga de la conexión
 *     propia si es líder, o retransmitido si es seguidora).
 *   - reenviarUltimo (default true): si false, la líder NO reenvía el último evento a las
 *     pestañas nuevas (p. ej. errores: no queremos re-mostrar un error viejo al abrir pestaña).
 * @returns {{desconectar: function}} desconectar() libera la conexión/lock de esta pestaña.
 */
function conectar(nombre, opciones) {
    var abrir = opciones.abrir;
    var onEvento = opciones.onEvento;
    var reenviarUltimo = opciones.reenviarUltimo !== false;

    if (!soportado()) {
        // Comportamiento clásico: conexión propia por pestaña.
        var directa = abrir(onEvento);
        return {
            desconectar: function () {
                if (directa) { try { directa.close(); } catch (e) {} directa = null; }
            }
        };
    }

    var bc = new BroadcastChannel('sse:' + nombre);
    var conexion = null;
    var soyLider = false;
    var activo = true;
    var ultimo;             // último payload visto por la líder (replay a seguidoras nuevas)
    var liberarLock = null;

    bc.onmessage = function (e) {
        var m = e.data || {};
        if (m.t === 'ev') {
            if (!soyLider) onEvento(m.p);
        } else if (m.t === 'hola' && soyLider && reenviarUltimo && ultimo !== undefined) {
            try { bc.postMessage({ t: 'ev', p: ultimo }); } catch (err) {}
        }
    };

    // Cola de liderazgo: la promesa interior retiene el lock hasta desconectar (o cerrar la pestaña).
    navigator.locks.request('sse-lider:' + nombre, function () {
        return new Promise(function (resolve) {
            if (!activo) { resolve(); return; }
            liberarLock = resolve;
            soyLider = true;
            conexion = abrir(function (payload) {
                ultimo = payload;
                onEvento(payload);
                try { bc.postMessage({ t: 'ev', p: payload }); } catch (err) {}
            });
        });
    }).catch(function () { /* lock abortado: sin liderazgo */ });

    // Pide el último estado a la líder actual (si la hay).
    try { bc.postMessage({ t: 'hola' }); } catch (e) {}

    return {
        desconectar: function () {
            activo = false;
            soyLider = false;
            if (conexion) { try { conexion.close(); } catch (e) {} conexion = null; }
            if (liberarLock) { liberarLock(); liberarLock = null; }
            try { bc.close(); } catch (e) {}
        }
    };
}

export default { conectar: conectar, soportado: soportado };
